import React, { useEffect, useState } from 'react';
import { Smartphone, Download, X, BellRing } from 'lucide-react';
import { toast } from 'sonner';
import { isIOS, isAndroid, isStandalone, canPromptInstall, onInstallAvailable, promptInstall } from '../utils/pwaInstall';
import { NotificationHelpSheet } from './NotificationHelpSheet';

const LS = 'rbr_install_guide_dismissed';
const SERIF = { fontFamily: "'Playfair Display', serif" };

// One-time nudge on phones (browser only, never inside the installed app): get Reflections onto the Home Screen
// so reminders work — Apple requires it on iPhone; Android gets a one-tap Install button.
export const InstallGuideCard = () => {
  const [show, setShow] = useState(false);
  const [help, setHelp] = useState(false);
  const [installable, setInstallable] = useState(canPromptInstall());
  useEffect(() => onInstallAvailable(setInstallable), []);
  useEffect(() => {
    if (isStandalone() || !(isIOS() || isAndroid())) return;
    if (localStorage.getItem(LS)) return;
    setShow(true);
  }, []);
  if (!show) return null;
  const dismiss = () => { localStorage.setItem(LS, '1'); setShow(false); };
  const install = async () => {
    const outcome = await promptInstall();
    if (outcome === 'accepted') { toast.success('Reflections installed — find it on your Home screen'); dismiss(); }
  };
  return (
    <section className="px-5 mb-4 animate-fade-in-up" data-testid="install-guide-card">
      <div className="relative rounded-3xl bg-[#FFFCF3] border border-[#D6AD60]/50 p-4 shadow-md">
        <button type="button" onClick={dismiss} aria-label="Not now" className="absolute top-3 right-3 w-8 h-8 rounded-full bg-[#E8DDCB] flex items-center justify-center" data-testid="install-guide-dismiss"><X className="w-4 h-4 text-[#0f1e33]" /></button>
        <div className="flex items-start gap-3 pr-8">
          <div className="w-11 h-11 rounded-2xl bg-[#173F35] flex items-center justify-center shrink-0"><Smartphone className="w-5 h-5 text-[#D6AD60]" /></div>
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#a88a4c]">Get the app feel</p>
            <h3 className="text-base text-[#0f1e33] mt-0.5" style={SERIF}>Add Reflections to your Home Screen</h3>
            <p className="text-xs text-[#2d4a70] mt-1 leading-relaxed">Opens like an app, no browser bar — and it's how daily reminders reach your phone{isIOS() ? ' (Apple requires this on iPhone)' : ''}.</p>
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          {installable ? (
            <button type="button" onClick={install} className="flex-1 h-11 rounded-full bg-[#0f1e33] text-[#f9f4ea] text-xs font-semibold uppercase tracking-[0.18em] inline-flex items-center justify-center gap-2" data-testid="install-guide-install"><Download className="w-4 h-4" /> Install app</button>
          ) : (
            <button type="button" onClick={() => setHelp(true)} className="flex-1 h-11 rounded-full bg-[#0f1e33] text-[#f9f4ea] text-xs font-semibold uppercase tracking-[0.18em] inline-flex items-center justify-center gap-2" data-testid="install-guide-how"><BellRing className="w-4 h-4" /> Show me how</button>
          )}
          <button type="button" onClick={dismiss} className="h-11 px-4 rounded-full border border-[#D6AD60]/50 text-[#0f1e33] text-xs font-semibold uppercase tracking-[0.18em]" data-testid="install-guide-later">Not now</button>
        </div>
      </div>
      <NotificationHelpSheet open={help} mode="install" onClose={() => { setHelp(false); }} />
    </section>
  );
};

export default InstallGuideCard;
